// Libraries
import { useCallback, useState } from "react";

// Components
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";

// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";

// Constants
import { REQUIRED_ACTIONS } from "@/Settings/Constants/PayrollMapping";

// Hooks
import { usePayrollMappingsContext } from "@/Settings/context/PayrollMapping";

// Tab Components
import PayItems from "./Tabs/PayItems";
import CustomFieldItems from "./Tabs/CustomFieldItems";

const CONTENT_TABS = [
	(isActive: boolean) => <PayItems key={"PayItems"} isActiveTab={isActive} />,
	(isActive: boolean) => <CustomFieldItems key={"CustomFieldItems"} isActiveTab={isActive} />,
];

type TabInfo = {
	title: string;
	index: number;
	disabled: boolean;
};

export default function PayrollMappingContainer() {
	const { tabs: contextTabs = [], setTabSelected: setTabSelectedContext } = usePayrollMappingsContext();
	const { useAuth } = useAuthorization();

	const [hasPayItemsAccess, hasCustomFieldItemsAccess] = REQUIRED_ACTIONS.map(useAuth);

	const isTabEnabled = useCallback(
		(key: string): boolean => {
			switch (key) {
				case "pay-items":
					return hasPayItemsAccess;
				case "custom-field-items":
					return true;

				default:
					return false;
			}
		},
		[hasPayItemsAccess, hasCustomFieldItemsAccess]
	);

	const tabs = contextTabs.map(({ key, label }, index) => ({ title: label, index, disabled: !isTabEnabled(key) }));
	const firstTabWithAccess = tabs.find((tab) => !tab.disabled) ?? { title: tabs?.[0]?.title ?? "", index: 0, disabled: true };

	const [tabSelected, setTabSelected] = useState<TabInfo>(() => ({ ...firstTabWithAccess }));

	function onTabChange(_: number, tabInfo: { label: TabInfo; index: number }) {
		setTabSelected(tabInfo.label);
		setTabSelectedContext?.(tabInfo.index);
	}

	return (
		<TabSelectable
			tabs={tabs}
			tabBarClassName={"!p-0 !m-0 !pb-8"}
			tabsProps={{
				tabClasses: "!min-w-[1100px]",
			}}
			content={CONTENT_TABS.map((content, index) => content(tabSelected.index === index))}
			tabSelected={tabSelected.index}
			onTabChange={onTabChange}
		/>
	);
}
